import { getPromozavrRequestKey, shouldInitPromozavr } from '../../../../../common/helpers';
import { isDarkTheme } from '../../helpers/theme';
import { getPromozavrProjectByQueue } from './queue-project';
import { type PromozavrQueueSelection, type PromozavrRendererSource, type PromozavrState } from './types';

export { getPromozavrProjectByQueue };

type ComputePromozavrRuntimeDecisionArgs = {
    pathname: string;
    search: string;
    isYandexNet: boolean;
    shouldUsePaas: boolean;
    shouldUsePdokPromoRenderer: boolean;
    isMessenger: boolean;
    isMeetingRoom: boolean;
};

type ShouldDispatchPromozavrInitArgs = {
    shouldInit: boolean;
    requestKey?: string;
    nextRequestKey: string;
};

type PromozavrDebugSnapshotArgs = Pick<
    PromozavrState,
    'status' | 'requestKey' | 'request' | 'queueSelections' | 'rendererSource' | 'lastError'
> & {
    pathname: string;
    shouldInit: boolean;
};

type BuildPromozavrRuntimeViewStateArgs = Pick<PromozavrState, 'requestKey' | 'queueSelections'> &
    ComputePromozavrRuntimeDecisionArgs & {
        bodyThemeName: string | null;
    };

type ShouldResetPromozavrStateArgs = Pick<
    PromozavrState,
    'status' | 'request' | 'requestKey' | 'queueSelections' | 'rendererSource' | 'lastError'
> & {
    shouldInit: boolean;
    nextRendererSource: PromozavrRendererSource;
};

type PromozavrDebugWindowLike = {
    __telemostPromozavr?: Record<string, unknown>;
};

function canRenderQueueSelection({
    shouldInit,
    requestKey,
    nextRequestKey,
    selection,
}: {
    shouldInit: boolean;
    requestKey?: string;
    nextRequestKey: string;
    selection: PromozavrQueueSelection;
}) {
    return Boolean(
        shouldInit &&
            requestKey === nextRequestKey &&
            selection.isSelected &&
            selection.selectedPromo &&
            selection.translations
    );
}

function hasQueueSelectionData(selection: PromozavrQueueSelection) {
    return (
        selection.selectedPromo !== undefined ||
        selection.translations !== undefined ||
        selection.images !== undefined ||
        selection.isSelected
    );
}

function buildResponseFromQueueSelection(selection: PromozavrQueueSelection) {
    return {
        selectedPromos: selection.selectedPromo ? [selection.selectedPromo] : [],
        translations: selection.translations,
        images: selection.images,
        isSelected: selection.isSelected,
    };
}

export function computePromozavrRuntimeDecision(
    {
        pathname,
        search,
        isYandexNet,
        shouldUsePaas,
        shouldUsePdokPromoRenderer,
        isMessenger,
        isMeetingRoom,
    }: ComputePromozavrRuntimeDecisionArgs,
    resolveRendererSource: (shouldUseRemoteRenderer: boolean) => PromozavrRendererSource
) {
    const hasDebugQuery = isYandexNet && new URLSearchParams(search).has('promozavr');
    const shouldInit = shouldInitPromozavr({
        isPaasEnabled: shouldUsePaas,
        pathname,
        isMessenger,
        isMeetingRoom,
    });
    const nextRequestKey = getPromozavrRequestKey(pathname, search);
    const nextRendererSource = resolveRendererSource(shouldInit && shouldUsePdokPromoRenderer);

    return {
        hasDebugQuery,
        shouldInit,
        nextRequestKey,
        nextRendererSource,
    };
}

export function buildPromozavrRuntimeViewState(
    { bodyThemeName, requestKey, queueSelections, ...runtimeDecisionArgs }: BuildPromozavrRuntimeViewStateArgs,
    resolveRendererSource: (shouldUseRemoteRenderer: boolean) => PromozavrRendererSource
) {
    const runtimeDecision = computePromozavrRuntimeDecision(runtimeDecisionArgs, resolveRendererSource);
    const shouldRenderRegular = canRenderQueueSelection({
        shouldInit: runtimeDecision.shouldInit,
        requestKey,
        nextRequestKey: runtimeDecision.nextRequestKey,
        selection: queueSelections.regular,
    });
    const shouldRenderPersistent = canRenderQueueSelection({
        shouldInit: runtimeDecision.shouldInit,
        requestKey,
        nextRequestKey: runtimeDecision.nextRequestKey,
        selection: queueSelections.persistent,
    });

    return {
        ...runtimeDecision,
        isDark: Boolean(isDarkTheme(bodyThemeName)),
        shouldRender: shouldRenderRegular || shouldRenderPersistent,
        shouldRenderRegular,
        shouldRenderPersistent,
    };
}

export function shouldDispatchPromozavrInit({
    shouldInit,
    requestKey,
    nextRequestKey,
}: ShouldDispatchPromozavrInitArgs) {
    return shouldInit && requestKey !== nextRequestKey;
}

export function shouldResetPromozavrState({
    shouldInit,
    status,
    request,
    requestKey,
    queueSelections,
    rendererSource,
    lastError,
    nextRendererSource,
}: ShouldResetPromozavrStateArgs) {
    if (shouldInit) {
        return false;
    }

    return (
        status !== 'idle' ||
        request !== undefined ||
        requestKey !== undefined ||
        hasQueueSelectionData(queueSelections.regular) ||
        hasQueueSelectionData(queueSelections.persistent) ||
        rendererSource !== nextRendererSource ||
        lastError !== undefined
    );
}

export function buildPromozavrDebugSnapshot({
    pathname,
    shouldInit,
    status,
    requestKey,
    request,
    queueSelections,
    rendererSource,
    lastError,
}: PromozavrDebugSnapshotArgs) {
    const regularResponse = buildResponseFromQueueSelection(queueSelections.regular);
    const persistentResponse = buildResponseFromQueueSelection(queueSelections.persistent);

    return {
        pathname,
        shouldInit,
        status,
        requestKey,
        request,
        response: {
            regular: regularResponse,
            persistent: persistentResponse,
        },
        queueSelections,
        rendererSource,
        lastError,
    };
}

export function syncPromozavrDebugSnapshot(
    windowLike: PromozavrDebugWindowLike,
    snapshot: Record<string, unknown>,
    enabled: boolean
) {
    if (!enabled) {
        delete windowLike.__telemostPromozavr;
        return;
    }

    windowLike.__telemostPromozavr = snapshot;
}

export function normalizePromozavrUrl(host: string) {
    const normalizedHost = host.replace(/\/+$/, '');

    if (normalizedHost.startsWith('http://') || normalizedHost.startsWith('https://')) {
        return normalizedHost;
    }

    return `https://${normalizedHost}`;
}

export function getPromozavrTuningUrl(yandex360Host: string) {
    return normalizePromozavrUrl(yandex360Host);
}

export function buildPromozavrGetNow(serverTime?: number) {
    const startedAt = Date.now();
    const delta = typeof serverTime === 'number' ? serverTime - startedAt : 0;

    return () => Date.now() + delta;
}
